import styled from '@emotion/styled';
import type { Chat, Widget } from '@trinity/chat';
import { ChatCitationType } from '@trinity/chat';
import { SystemMessageContainer } from '@trinity/chat-components/components/Chat/Message.System.Container';
import { ChatResponsePane } from '@trinity/chat-components/components/Chat/ResponsePane';
import { useToggle } from '@trinity/chat-components/hooks/useToggle';
import { TranscriptMessageCitations } from '../Chat.Message.Citations';

type Props = {
  SourcesComponent?: React.ComponentType<Pick<Widget.TagSummaryProps, 'citations' | 'source'>>;
} & Widget.TagSummaryProps;

export const WidgetTagSummaryCard = ({ SourcesComponent, ...props }: Props) => {

  const citations = buildCitations(props);

  return (
    <Root>
      <UnorderedList items={props.items} />
      <SourcePanel citations={citations} />
    </Root>
  );
};

WidgetTagSummaryCard.displayName = 'Widget.TagSummary.Card';

const UnorderedList = (props: Pick<Props, 'items'>) => {
  if (!props.items?.length) return null;

  return (
    <UL>
      {props.items.map((bullet, i) =>
        <LI key={i}>
          {bullet.text}
          <UnorderedList items={bullet.items} />
        </LI>)}
    </UL>
  );
};

const Root = styled.div`

`;

const UL = styled.ul({
  paddingInlineStart: '20px',
  margin: 0,
});

const LI = styled.li({
  padding: '5px 0',
});

const SourcePanel = (props: { citations: Chat.Citation[] }) => {
  const [open, toggleOpen] = useToggle(false);

  if (!props.citations?.length) return null;

  return (
    <SourceRoot>
      <SystemMessageContainer citations={props.citations}>
        <StyledCitationsPane
          title={`Sources (${props.citations.length})`}
          open={open}
          toggleOpen={toggleOpen}>
          <StyledMessageCitations
            items={props.citations} />
        </StyledCitationsPane>
      </SystemMessageContainer>
    </SourceRoot>
  );
};

const SourceRoot = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
  width: 100%;
`;

const StyledCitationsPane = styled(ChatResponsePane)`
  margin-top: 15px;
`;

const StyledMessageCitations = styled(TranscriptMessageCitations)`
  margin-top: 10px;
`;

function buildCitations(params: Pick<Widget.TagSummaryProps, 'citations' | 'source'>) {
  if (!params?.citations?.length) return [];

  return params.citations.map((x, i) => {
    return {
      ordinal: x.ordinal,
      id: x.id,
      link: x.source.link,
      type: ChatCitationType.Transcript,
      content: buildSourceSnippet(x),
      score: null,
      title: `Transcript (${x.source.transcriptId})`,
      snippets: [],
    } as unknown as Chat.Citation;
  });
}

const buildSourceSnippet = (item: Widget.TagSummaryProps['citations'][number]) => {
  if (!item?.monologues?.length) return [];

  return item.monologues.reduce((acc, x) => {
    return `${acc}#### ${x.speakerName}\n${x.content}\n`;
  }, '');
};
